/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { BEM } from "@adapptio/lib-components";
import { Input } from "../Input";
import { InputAdornment } from "../InputAdornment";
import { InputButton } from "../InputButton";
import { Icon, ICON_SIZE } from "../Icon";

export interface ISelectInputProps {
	className?: string;
	id?: string;
	open?: boolean;
	inputRef?: React.Ref<any>;
	preventEdit?: boolean;
	placeholder?: string;
	value?: string;
	icon?: string;
	onChange?: React.ChangeEventHandler;
	onKeyDown?: React.KeyboardEventHandler;
	startAdornment?: React.ReactNode;
	endAdornment?: React.ReactNode;
	disabled?: boolean;
	readOnly?: boolean;
	onTriggerClick?: React.MouseEventHandler;

	[arbitrary: string]: any;
}

export const SelectInput: React.FC<ISelectInputProps> = ({
	className,
	open,
	startAdornment,
	endAdornment,
	onTriggerClick,
	icon,
	...props
}) => {
	const baseClass = BEM("edt-select-input", null, {
		open
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<Input
			type="text"
			className={baseClass.toClassName()}
			startAdornment={
				startAdornment || icon ? (
					<>
						{startAdornment}
						{icon && (
							<InputAdornment square center>
								<Icon
									className={baseClass.element("icon").toClassName()}
									name={icon}
									size={ICON_SIZE.XSMALL}
								/>
							</InputAdornment>
						)}
					</>
				) : null
			}
			endAdornment={
				<>
					{endAdornment}
					<InputAdornment>
						<InputButton
							className={baseClass.element("dropdown-trigger").toClassName()}
							onClick={onTriggerClick}
							tabIndex={-1}
							disabled={props.disabled || props.readOnly}
						>
							<Icon
								className={baseClass.element("dropdown-trigger-icon").toClassName()}
								name="mdi/menu-down"
								size={ICON_SIZE.XSMALL}
							/>
						</InputButton>
					</InputAdornment>
				</>
			}
			{...props}
		/>
	);
};
